import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import {
  CutsheetCapabilityOption,
  CutsheetCapabilitySpecification,
  CutsheetQuestionnaire,
  useCutsheetCapabilityOptionsManyQuery,
  useCutsheetCapabilitySetLegacyMapMutation,
  useCutsheetCapabilitySpecificationManyQuery,
  useCutsheetLegacyCapabilityDataLazyQuery,
} from 'lib/graphql';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface LegacyMapEntry {
  inspectionLevel: string;
  questionnaireId: string;
  cutMappings: Record<string, string>; // oldCutId → newOptionId
  trimMappings: Record<string, string>; // oldTrimName → newOptionId
  extraMappings: Record<string, { specId: string; value: string }>; // oldExtraName → { specId, value }
  specMappings: Record<string, string>; // oldSpecName → newSpecificationId
}

interface LegacyMappingModalProps {
  capabilityId: string;
  questionnaires: CutsheetQuestionnaire[];
  existingMap?: Array<{
    inspectionLevel: string;
    questionnaireId: string;
    cutMappings?: Array<{
      oldCutId: string;
      newOptionId: string;
    } | null> | null;
    trimMappings?: Array<{
      oldTrimName: string;
      newOptionId: string;
    } | null> | null;
    extraMappings?: Array<{
      oldExtraName: string;
      newSpecificationId: string;
      newOptionValue: string;
    } | null> | null;
    specMappings?: Array<{
      oldSpecName: string;
      newSpecificationId: string;
    } | null> | null;
  } | null> | null;
  /** When provided, only these inspection levels are surfaced as tabs. */
  enabledInspectionLevels?: string[];
  onComplete: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatLevel(level: string): string {
  const labels: Record<string, string> = {
    exempt: 'Custom Exempt',
    usda: 'USDA',
    state: 'State Inspected',
  };
  return (
    labels[level] ??
    level
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const LegacyMappingModal: FC<LegacyMappingModalProps> = ({
  capabilityId,
  questionnaires,
  existingMap,
  enabledInspectionLevels,
  onComplete,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [mapping, setMapping] = useState<Record<string, LegacyMapEntry>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load legacy data
  const [loadLegacy, { data: legacyData, loading: legacyLoading }] =
    useCutsheetLegacyCapabilityDataLazyQuery();

  // Load new capability options and specs
  const { data: optionsData, loading: optionsLoading } =
    useCutsheetCapabilityOptionsManyQuery({
      variables: { capabilityId },
    });

  const { data: specsData, loading: specsLoading } =
    useCutsheetCapabilitySpecificationManyQuery({
      variables: { capabilityId },
    });

  const [setLegacyMap] = useCutsheetCapabilitySetLegacyMapMutation();

  useEffect(() => {
    loadLegacy({ variables: { capabilityId } });
  }, [capabilityId]);

  const enabledLevelSet = useMemo(
    () =>
      enabledInspectionLevels && enabledInspectionLevels.length > 0
        ? new Set(enabledInspectionLevels)
        : null,
    [enabledInspectionLevels],
  );

  const inspectionLevels = (legacyData?.CutsheetLegacyCapabilityData ?? [])
    .filter((d): d is NonNullable<typeof d> => Boolean(d))
    .map((d) => d.inspectionLevel)
    .filter((lvl) => !enabledLevelSet || enabledLevelSet.has(lvl));

  // New options split by type
  const newCuts = useMemo(
    () =>
      (optionsData?.cutsheetCapabilityOptionsByVendorAndAnimal ?? []).filter(
        (o): o is CutsheetCapabilityOption => o?.optionType === 'cut',
      ),
    [optionsData],
  );
  const newTrims = useMemo(
    () =>
      (optionsData?.cutsheetCapabilityOptionsByVendorAndAnimal ?? []).filter(
        (o): o is CutsheetCapabilityOption => o?.optionType === 'trim',
      ),
    [optionsData],
  );
  const newSpecs = useMemo(
    () =>
      (specsData?.cutsheetSpecificationsByVendorAndAnimal ?? []).filter(
        Boolean,
      ) as CutsheetCapabilitySpecification[],
    [specsData],
  );

  // Initialize mapping from existing data once legacy levels are loaded
  useEffect(() => {
    if (inspectionLevels.length === 0) return;
    if (Object.keys(mapping).length > 0) return; // already initialized

    const initial: Record<string, LegacyMapEntry> = {};
    for (const level of inspectionLevels) {
      const existing = existingMap?.find((e) => e?.inspectionLevel === level);
      initial[level] = {
        inspectionLevel: level,
        questionnaireId: existing?.questionnaireId ?? '',
        cutMappings: Object.fromEntries(
          (existing?.cutMappings ?? [])
            .filter((m): m is NonNullable<typeof m> => Boolean(m))
            .map((m) => [m.oldCutId, m.newOptionId]),
        ),
        trimMappings: Object.fromEntries(
          (existing?.trimMappings ?? [])
            .filter((m): m is NonNullable<typeof m> => Boolean(m))
            .map((m) => [m.oldTrimName, m.newOptionId]),
        ),
        extraMappings: Object.fromEntries(
          (existing?.extraMappings ?? [])
            .filter((m): m is NonNullable<typeof m> => Boolean(m))
            .map((m) => [
              m.oldExtraName,
              { specId: m.newSpecificationId, value: m.newOptionValue },
            ]),
        ),
        specMappings: Object.fromEntries(
          (existing?.specMappings ?? [])
            .filter((m): m is NonNullable<typeof m> => Boolean(m))
            .map((m) => [m.oldSpecName, m.newSpecificationId]),
        ),
      };
    }
    setMapping(initial);
  }, [inspectionLevels, existingMap]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const entries = Object.values(mapping)
        .filter((entry) => entry.questionnaireId)
        .map((entry) => ({
          inspectionLevel: entry.inspectionLevel,
          questionnaireId: entry.questionnaireId,
          cutMappings: Object.entries(entry.cutMappings)
            .filter(([, v]) => v)
            .map(([oldCutId, newOptionId]) => ({ oldCutId, newOptionId })),
          trimMappings: Object.entries(entry.trimMappings)
            .filter(([, v]) => v)
            .map(([oldTrimName, newOptionId]) => ({
              oldTrimName,
              newOptionId,
            })),
          extraMappings: Object.entries(entry.extraMappings)
            .filter(([, v]) => v.specId && v.value)
            .map(([oldExtraName, { specId, value }]) => ({
              oldExtraName,
              newSpecificationId: specId,
              newOptionValue: value,
            })),
          specMappings: Object.entries(entry.specMappings)
            .filter(([, v]) => v)
            .map(([oldSpecName, newSpecificationId]) => ({
              oldSpecName,
              newSpecificationId,
            })),
        }));

      await setLegacyMap({
        variables: {
          id: capabilityId,
          legacyInspectionLevelMap: entries,
        },
      });
      setSuccess(true);
      onComplete();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save mapping.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6">Mapping Saved</Typography>
        <Typography variant="body2" color="text.secondary">
          Legacy item mappings have been updated.
        </Typography>
      </Box>
    );
  }

  if (legacyLoading || optionsLoading || specsLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading legacy data…
        </Typography>
      </Box>
    );
  }

  if (inspectionLevels.length === 0) {
    return (
      <Alert severity="warning">
        No legacy ProcessorCapability data found for this species and vendor.
      </Alert>
    );
  }

  const currentLevel = inspectionLevels[activeTab];
  const legacyLevel = legacyData?.CutsheetLegacyCapabilityData?.find(
    (d) => d?.inspectionLevel === currentLevel,
  );
  const currentEntry = mapping[currentLevel];

  const updateEntry = (level: string, updates: Partial<LegacyMapEntry>) => {
    setMapping((prev) => ({
      ...prev,
      [level]: { ...prev[level], ...updates },
    }));
  };

  const updateCutMapping = (oldCutId: string, newOptionId: string) => {
    updateEntry(currentLevel, {
      cutMappings: {
        ...currentEntry?.cutMappings,
        [oldCutId]: newOptionId,
      },
    });
  };

  const updateTrimMapping = (oldName: string, newOptionId: string) => {
    updateEntry(currentLevel, {
      trimMappings: {
        ...currentEntry?.trimMappings,
        [oldName]: newOptionId,
      },
    });
  };

  const updateExtraMapping = (
    oldName: string,
    specId: string,
    value: string,
  ) => {
    updateEntry(currentLevel, {
      extraMappings: {
        ...currentEntry?.extraMappings,
        [oldName]: { specId, value },
      },
    });
  };

  const updateSpecMapping = (oldName: string, newSpecId: string) => {
    updateEntry(currentLevel, {
      specMappings: {
        ...currentEntry?.specMappings,
        [oldName]: newSpecId,
      },
    });
  };

  return (
    <Stack spacing={2}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {inspectionLevels.map((level) => (
          <Tab key={level} label={formatLevel(level)} />
        ))}
      </Tabs>

      {currentEntry && legacyLevel && (
        <Stack spacing={3} sx={{ maxHeight: '60vh', overflow: 'auto', pr: 1 }}>
          {/* Questionnaire mapping */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Cutsheet Form
            </Typography>
            <Autocomplete
              size="small"
              options={questionnaires}
              getOptionLabel={(q) => q.name ?? 'Unnamed'}
              value={
                questionnaires.find(
                  (q) => q._id === currentEntry.questionnaireId,
                ) ?? null
              }
              onChange={(_, val) =>
                updateEntry(currentLevel, {
                  questionnaireId: val?._id ?? '',
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Target Questionnaire" />
              )}
            />
          </Box>

          {/* Cut mappings — grouped by primal */}
          {legacyLevel.cuts.length > 0 &&
            (() => {
              const validCuts = legacyLevel.cuts.filter(Boolean);
              const cutsByPrimal = new Map<
                string,
                NonNullable<(typeof legacyLevel.cuts)[number]>[]
              >();
              for (const cut of validCuts) {
                const group = cut!.primalName || 'Other';
                if (!cutsByPrimal.has(group)) cutsByPrimal.set(group, []);
                cutsByPrimal.get(group)!.push(cut!);
              }

              return (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Cuts ({legacyLevel.cuts.length})
                  </Typography>
                  <Stack spacing={2}>
                    {Array.from(cutsByPrimal.entries()).map(
                      ([primalName, cuts]) => (
                        <Box key={primalName}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {primalName}
                          </Typography>
                          <Stack spacing={1.5} sx={{ pl: 2 }}>
                            {cuts.map((cut) => (
                              <Autocomplete
                                key={cut._id}
                                size="small"
                                options={newCuts}
                                getOptionLabel={(o) => o.title ?? 'Unnamed'}
                                value={
                                  newCuts.find(
                                    (o) =>
                                      o._id ===
                                      currentEntry.cutMappings[cut._id],
                                  ) ?? null
                                }
                                onChange={(_, val) =>
                                  updateCutMapping(cut._id, val?._id ?? '')
                                }
                                renderInput={(params) => (
                                  <TextField {...params} label={cut.name} />
                                )}
                              />
                            ))}
                          </Stack>
                        </Box>
                      ),
                    )}
                  </Stack>
                </Box>
              );
            })()}

          {/* Trim mappings */}
          {legacyLevel.trims.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Trims ({legacyLevel.trims.length})
              </Typography>
              <Stack spacing={1.5}>
                {legacyLevel.trims.filter(Boolean).map((trim) => (
                  <Autocomplete
                    key={trim!.name}
                    size="small"
                    options={newTrims}
                    getOptionLabel={(o) => o.title ?? 'Unnamed'}
                    value={
                      newTrims.find(
                        (o) => o._id === currentEntry.trimMappings[trim!.name],
                      ) ?? null
                    }
                    onChange={(_, val) =>
                      updateTrimMapping(trim!.name, val?._id ?? '')
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={trim!.name} />
                    )}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Extra mappings */}
          {legacyLevel.extras.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Extras ({legacyLevel.extras.length})
              </Typography>
              <Stack spacing={1.5}>
                {legacyLevel.extras.filter(Boolean).map((extra) => {
                  const current = currentEntry.extraMappings[extra!.name];
                  return (
                    <Stack key={extra!.name} direction="row" spacing={1}>
                      <Autocomplete
                        size="small"
                        sx={{ flex: 1 }}
                        options={newSpecs}
                        getOptionLabel={(s) => s.title ?? 'Unnamed'}
                        value={
                          newSpecs.find((s) => s._id === current?.specId) ??
                          null
                        }
                        onChange={(_, val) =>
                          updateExtraMapping(
                            extra!.name,
                            val?._id ?? '',
                            current?.value ?? '',
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${extra!.name} → Specification`}
                          />
                        )}
                      />
                      <TextField
                        size="small"
                        sx={{ flex: 1 }}
                        label="Option Value"
                        value={current?.value ?? ''}
                        onChange={(e) =>
                          updateExtraMapping(
                            extra!.name,
                            current?.specId ?? '',
                            e.target.value,
                          )
                        }
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Specification mappings */}
          {legacyLevel.specifications.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Specifications ({legacyLevel.specifications.length})
              </Typography>
              <Stack spacing={1.5}>
                {legacyLevel.specifications.filter(Boolean).map((spec) => (
                  <Autocomplete
                    key={spec!.name}
                    size="small"
                    options={newSpecs}
                    getOptionLabel={(s) => s.title ?? 'Unnamed'}
                    value={
                      newSpecs.find(
                        (s) => s._id === currentEntry.specMappings[spec!.name],
                      ) ?? null
                    }
                    onChange={(_, val) =>
                      updateSpecMapping(spec!.name, val?._id ?? '')
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={spec!.name} />
                    )}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={loading}
        sx={{ mt: 2 }}
      >
        {loading ? 'Saving…' : 'Save Mapping'}
      </Button>
    </Stack>
  );
};
